(() => {
  const moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'EUR',
  });

  const money = (cents) => window.CloudlessMoney?.format(cents) ?? moneyFormatter.format((Number(cents) || 0) / 100);

  function initProductHeader() {
    const header = document.querySelector('.cl-header');
    if (!header || header.dataset.clpProductHeader === 'true') return;
    header.dataset.clpProductHeader = 'true';
    let frame = null;
    const render = () => {
      frame = null;
      header.toggleAttribute('data-clp-solid', window.scrollY > 8);
      const headerHeight = header.getBoundingClientRect().height;
      const parallaxActive = window.innerWidth <= 860 && [...document.querySelectorAll('[data-clp-science]')].some((section) => {
        const rect = section.getBoundingClientRect();
        return rect.top <= headerHeight && rect.bottom > 0;
      });
      header.toggleAttribute('data-clp-parallax-hidden', parallaxActive);
      if (parallaxActive) header.setAttribute('data-hidden', '');
    };
    const requestRender = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(render);
    };
    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', requestRender, { passive: true });
    render();
  }

  function initRevealHeading(heading) {
    if (!heading || heading.dataset.clpRevealInitialized === 'true') return;
    heading.dataset.clpRevealInitialized = 'true';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);
    const words = [];

    textNodes.forEach((textNode) => {
      const parts = textNode.textContent.split(/(\s+)/);
      const fragment = document.createDocumentFragment();
      parts.forEach((part) => {
        if (!part.trim()) {
          fragment.appendChild(document.createTextNode(part));
          return;
        }
        const span = document.createElement('span');
        span.className = 'clp-reveal-word';
        span.textContent = part;
        fragment.appendChild(span);
        words.push(span);
      });
      textNode.replaceWith(fragment);
    });

    if (reducedMotion || !words.length) {
      words.forEach((word) => word.style.setProperty('--clp-reveal-progress', '100%'));
      return;
    }

    let frame = null;
    const render = () => {
      frame = null;
      const rect = heading.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const start = viewportHeight * .88;
      const end = viewportHeight * .32;
      const progress = Math.min(1, Math.max(0, (start - rect.top) / (start - end)));
      const head = (progress * (words.length + 2)) - 1;
      words.forEach((word, index) => {
        const fill = Math.min(1, Math.max(0, head - index + 1));
        word.style.setProperty('--clp-reveal-progress', `${(fill * 100).toFixed(1)}%`);
      });
    };
    const requestRender = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(render);
    };
    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', requestRender, { passive: true });
    render();
  }

  function initGallery(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const track = root.querySelector('[data-clp-gallery-track]');
    const viewport = root.querySelector('[data-clp-gallery-viewport]');
    const slides = [...root.querySelectorAll('[data-clp-gallery-slide]')];
    const thumbs = [...root.querySelectorAll('[data-clp-gallery-thumb]')];
    const dots = [...root.querySelectorAll('[data-clp-gallery-dot]')];
    const rail = root.querySelector('[data-clp-gallery-rail]');
    const next = root.querySelector('[data-clp-gallery-next]');
    if (!track || !slides.length) return;

    let active = 0;
    let autoplayTimer = null;
    let isInViewport = false;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplay = root.hasAttribute('data-clp-gallery-autoplay');
    const loop = slides.length > 1;

    // Endless loop: a copy of the last slide before the first and of the first after the last. After sliding onto
    // a copy, the track jumps (without animation) to the real slide it shows.
    if (loop) {
      const copy = (slide) => {
        const clone = slide.cloneNode(true);
        clone.removeAttribute('data-clp-gallery-slide');
        clone.removeAttribute('data-media-id');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('img').forEach((img) => img.setAttribute('loading', 'lazy'));
        clone.querySelectorAll('video').forEach((video) => { video.removeAttribute('autoplay'); video.preload = 'none'; });
        return clone;
      };
      track.prepend(copy(slides[slides.length - 1]));
      track.append(copy(slides[0]));
    }
    let position = loop ? 1 : 0;

    const place = (offset = 0, animate = true) => {
      track.style.transition = animate ? '' : 'none';
      track.style.transform = offset
        ? `translateX(calc(${-position * 100}% + ${offset}px))`
        : `translateX(${-position * 100}%)`;
    };

    const settle = () => {
      if (!loop) return;
      if (position === 0 || position === slides.length + 1) {
        position = active + 1;
        place(0, false);
        void track.offsetWidth;
        track.style.transition = '';
      }
    };
    track.addEventListener('transitionend', (event) => {
      if (event.target === track) settle();
    });

    const stopAutoplay = () => {
      window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
      root.classList.remove('is-playing');
    };

    const scheduleAutoplay = () => {
      stopAutoplay();
      if (!autoplay || reducedMotion || !isInViewport || slides.length < 2 || document.hidden) return;
      void root.offsetWidth;
      root.classList.add('is-playing');
      autoplayTimer = window.setTimeout(() => select(active + 1), 5500);
    };

    const updateNavigation = () => {
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === active);
        thumb.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === active));
      const activeThumb = thumbs[active];
      if (activeThumb && rail) {
        const thumbLeft = activeThumb.offsetLeft;
        const thumbRight = thumbLeft + activeThumb.offsetWidth;
        const railLeft = rail.scrollLeft;
        const railRight = railLeft + rail.clientWidth;
        if (thumbLeft < railLeft || thumbRight > railRight) {
          rail.scrollTo({
            left: Math.max(0, thumbLeft - ((rail.clientWidth - activeThumb.offsetWidth) / 2)),
            behavior: 'smooth',
          });
        }
      }
    };

    // index may be -1 or slides.length (one step past either end): the loop then slides onto the copy.
    const select = (index, animate = true) => {
      settle();
      active = (index + slides.length) % slides.length;
      position = loop ? index + 1 : active;
      if (loop && (index < -1 || index > slides.length)) position = active + 1;
      place(0, animate && !reducedMotion);
      if (!animate || reducedMotion) settle();
      updateNavigation();
      scheduleAutoplay();
    };

    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => select(index)));
    dots.forEach((dot, index) => dot.addEventListener('click', () => select(index)));
    next?.addEventListener('click', () => {
      const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
      rail.scrollTo({ left: atEnd ? 0 : rail.scrollLeft + 176, behavior: 'smooth' });
    });

    // Drag / swipe: the slide follows the finger or mouse; releasing past 15% of the width (or a quick flick)
    // moves one slide, otherwise it springs back. Vertical swipes are left to the page (touch-action: pan-y).
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let deltaX = 0;
    let axis = null;

    viewport?.addEventListener('dragstart', (event) => event.preventDefault());
    viewport?.addEventListener('pointerdown', (event) => {
      if (!loop || (event.pointerType === 'mouse' && event.button !== 0)) return;
      settle();
      stopAutoplay();
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startTime = performance.now();
      deltaX = 0;
      axis = null;
    });
    viewport?.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (!axis) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis === 'x') viewport.setPointerCapture(pointerId);
      }
      if (axis !== 'x') return;
      event.preventDefault();
      deltaX = dx;
      place(deltaX, false);
    });
    const release = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      if (axis !== 'x') {
        scheduleAutoplay();
        return;
      }
      const width = viewport.clientWidth || 1;
      const quickFlick = Math.abs(deltaX) > 30 && performance.now() - startTime < 250;
      if (Math.abs(deltaX) > width * 0.15 || quickFlick) {
        select(active + (deltaX < 0 ? 1 : -1));
      } else {
        place(0, true);
        scheduleAutoplay();
      }
      axis = null;
    };
    viewport?.addEventListener('pointerup', release);
    viewport?.addEventListener('pointercancel', release);
    viewport?.addEventListener('click', (event) => {
      if (Math.abs(deltaX) > 6) {
        event.preventDefault();
        event.stopPropagation();
        deltaX = 0;
      }
    }, true);
    if ('IntersectionObserver' in window && slides.length > 1) {
      const observer = new IntersectionObserver(([entry]) => {
        isInViewport = entry.isIntersecting;
        scheduleAutoplay();
      }, { threshold: .4 });
      observer.observe(viewport || root);
    }
    document.addEventListener('visibilitychange', scheduleAutoplay);
    select(0, false);
  }

  function initProduct(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    // Kaching and buy box v2 run their own buy box scripts; only the sticky bar is shared.
    if (root.hasAttribute('data-clp-kaching') || root.hasAttribute('data-clp-v2')) {
      initStickyBar(root);
      return;
    }

    const form = root.querySelector('form[action*="/cart/add"]');
    const idInput = form?.querySelector('[name="id"]');
    const sellingPlanInput = form?.querySelector('[name="selling_plan"]');
    const plans = [...root.querySelectorAll('[data-clp-plan]')];
    const subscription = root.querySelector('[data-clp-subscription]');
    const toggle = root.querySelector('[data-clp-subscription-toggle]');
    const cadence = root.querySelector('[data-clp-cadence]');
    const atcPrice = root.querySelector('[data-clp-atc-price]');
    const atcCompare = root.querySelector('[data-clp-atc-compare]');
    const button = root.querySelector('[data-clp-add-button]');
    const totalLabel = root.dataset.totalLabel || 'totaal';
    const deliveryOne = subscription?.dataset.deliveryOne || 'Elke maand bezorgd';
    const deliveryMany = subscription?.dataset.deliveryMany || 'Elke [months] maanden bezorgd';
    let selected = plans.find((plan) => plan.classList.contains('is-selected')) || plans[0];
    let subscribed = toggle?.getAttribute('aria-checked') === 'true';

    const removeInjectedBundles = () => {
      root.querySelectorAll('kaching-bundle, kaching-bundles-block').forEach((element) => element.remove());
      form?.querySelectorAll('input[name*="__kaching_bundles"]').forEach((input) => input.remove());
    };
    removeInjectedBundles();
    const bundleObserver = new MutationObserver(removeInjectedBundles);
    bundleObserver.observe(root, { childList: true, subtree: true });

    const update = () => {
      if (!selected) return;
      const oneTimePrice = Number(selected.dataset.price || 0);
      const comparePrice = Number(selected.dataset.comparePrice || 0);
      const sellingPrice = Number(selected.dataset.sellingPrice || oneTimePrice);
      const sellingPlanId = selected.dataset.sellingPlanId || '';
      const months = Number(selected.dataset.months || 1);
      const selectedPrice = subscribed && sellingPlanId ? sellingPrice : oneTimePrice;
      const perMonth = Math.round(selectedPrice / Math.max(1, months));

      plans.forEach((plan) => {
        const isSelected = plan === selected;
        plan.classList.toggle('is-selected', isSelected);
        plan.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        const price = subscribed && plan.dataset.sellingPlanId
          ? Number(plan.dataset.sellingPrice || plan.dataset.price)
          : Number(plan.dataset.price);
        const divisor = Math.max(1, Number(plan.dataset.months || 1));
        const priceNode = plan.querySelector('[data-clp-plan-price]');
        const totalNode = plan.querySelector('[data-clp-plan-total]');
        if (priceNode) priceNode.firstChild.textContent = money(Math.round(price / divisor));
        if (totalNode) totalNode.textContent = `${money(price)} ${totalLabel}`;
      });

      if (idInput) idInput.value = selected.dataset.variantId;
      if (sellingPlanInput) {
        sellingPlanInput.disabled = !(subscribed && sellingPlanId);
        sellingPlanInput.value = subscribed && sellingPlanId ? sellingPlanId : '';
      }

      const canSubscribe = Boolean(sellingPlanId);
      if (toggle) {
        toggle.disabled = !canSubscribe;
        toggle.setAttribute('aria-disabled', canSubscribe ? 'false' : 'true');
        if (!canSubscribe) subscribed = false;
        toggle.setAttribute('aria-checked', subscribed ? 'true' : 'false');
      }
      subscription?.classList.toggle('is-active', subscribed);
      root.querySelectorAll('[data-clp-klarna-focus]').forEach((element) => { element.hidden = subscribed; });
      root.querySelectorAll('[data-clp-klarna-hide]').forEach((element) => { element.hidden = !subscribed; });
      root.querySelectorAll('[data-clp-klarna-amount]').forEach((element) => {
        element.textContent = money(Math.ceil(selectedPrice / 3));
      });
      if (cadence) cadence.textContent = months === 1 ? deliveryOne : deliveryMany.replace('[months]', months);
      if (atcPrice) atcPrice.textContent = money(selectedPrice);
      if (atcCompare) {
        atcCompare.textContent = comparePrice > selectedPrice ? money(comparePrice) : '';
        atcCompare.hidden = comparePrice <= selectedPrice;
      }
      if (button) {
        const available = selected.dataset.available === 'true';
        button.disabled = !available;
        button.querySelector('[data-clp-add-label]').textContent = available
          ? button.dataset.availableLabel
          : button.dataset.soldOutLabel;
      }
      const event = new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant: { id: Number(selected.dataset.variantId) } },
      });
      form?.dispatchEvent(event);
    };

    plans.forEach((plan) => plan.addEventListener('click', () => {
      selected = plan;
      update();
    }));
    toggle?.addEventListener('click', () => {
      if (toggle.disabled) return;
      subscribed = !subscribed;
      update();
    });
    form?.addEventListener('submit', () => {
      removeInjectedBundles();
      update();
    }, { capture: true });
    initStickyBar(root);
    update();
  }

  function initStickyBar(root) {
    const stickyBar = root.querySelector('[data-clp-sticky-atc]');
    if (!stickyBar) return;
    stickyBar.querySelector('a')?.addEventListener('click', (event) => {
      event.preventDefault();
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    // Show the bar only once the add-to-cart button (buy box v2: the whole buy box) has scrolled out of view above the viewport
    const addButton = root.querySelector('[data-clbx]') || root.querySelector('[data-clp-add-button]') || root.querySelector('.clp-plans');
    const targets = [addButton, document.querySelector('[data-clp-video]'), document.querySelector('.shopify-section-group-footer-group')].filter(Boolean);
    const hides = new Map();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const notYetPassed = entry.target === addButton && entry.boundingClientRect.top > 0;
        hides.set(entry.target, entry.isIntersecting || notYetPassed);
      });
      stickyBar.classList.toggle('is-hidden', [...hides.values()].some(Boolean));
    }, { threshold: 0 });
    targets.forEach((target) => observer.observe(target));
  }

  function initDragRail(rail) {
    if (!rail || rail.dataset.initialized === 'true') return;
    rail.dataset.initialized = 'true';
    let down = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;

    rail.style.touchAction = 'pan-y pinch-zoom';
    rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      down = true;
      moved = false;
      startX = event.clientX;
      startScroll = rail.scrollLeft;
      rail.style.cursor = 'grabbing';
    });
    rail.addEventListener('pointermove', (event) => {
      if (!down) return;
      const delta = event.clientX - startX;
      if (Math.abs(delta) > 3) moved = true;
      if (moved) {
        if (event.cancelable) event.preventDefault();
        rail.scrollLeft = startScroll - delta;
      }
    }, { passive: false });
    const end = () => {
      down = false;
      rail.style.cursor = 'grab';
    };
    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointercancel', end);
    rail.addEventListener('pointerleave', end);
    rail.addEventListener('click', (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    }, true);
  }

  function initAutoMarquee(rail) {
    if (!rail || rail.dataset.autoInitialized === 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    rail.dataset.autoInitialized = 'true';
    // The content is rendered twice; keep the scroll position inside the first copy so there is always room left and right.
    const direction = Number(rail.dataset.direction || 1);
    const pixelsPerMs = (Number(rail.dataset.speed) || 25) / 1000;
    const resumeDelay = 1500;
    const half = () => rail.scrollWidth / 2;
    // Allowed window [low, low + half): equal room on both sides, so wrapping never lands on a scroll edge.
    const wrap = (value) => {
      const size = half();
      if (size <= rail.clientWidth) return value;
      const low = (size - rail.clientWidth) / 2;
      while (value < low) value += size;
      while (value >= low + size) value -= size;
      return value;
    };
    let position = half();
    let lastSet = -1;
    let lastTime = 0;
    let holding = false;
    let resumeAt = 0;
    const setScroll = (value) => {
      position = value;
      rail.scrollLeft = value;
      lastSet = rail.scrollLeft;
    };
    const pauseFor = (ms) => { resumeAt = Math.max(resumeAt, performance.now() + ms); };
    setScroll(position);

    rail.addEventListener('scroll', () => {
      if (Math.abs(rail.scrollLeft - lastSet) < 1) return;
      pauseFor(resumeDelay);
      const wrapped = wrap(rail.scrollLeft);
      if (wrapped !== rail.scrollLeft) setScroll(wrapped);
      else position = rail.scrollLeft;
    }, { passive: true });

    const tick = (time) => {
      const idle = !holding && time >= resumeAt;
      if (idle && lastTime) {
        setScroll(wrap(position + direction * Math.min(32, time - lastTime) * pixelsPerMs));
      }
      lastTime = time;
      requestAnimationFrame(tick);
    };
    const hold = () => { holding = true; };
    const release = () => { holding = false; pauseFor(resumeDelay); };
    rail.addEventListener('pointerdown', hold);
    rail.addEventListener('pointerup', release);
    rail.addEventListener('pointercancel', release);
    rail.addEventListener('touchstart', hold, { passive: true });
    rail.addEventListener('touchend', release, { passive: true });
    rail.addEventListener('mouseenter', hold);
    rail.addEventListener('mouseleave', release);
    rail.addEventListener('wheel', () => pauseFor(resumeDelay), { passive: true });
    requestAnimationFrame(tick);
  }


  function initIngredients(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    const cards = root.querySelectorAll('[data-clp-ingredient-card]');
    if ('IntersectionObserver' in window && cards.length) {
      const revealObserver = new IntersectionObserver((entries) => {
        entries.filter((entry) => entry.isIntersecting).forEach((entry, index) => {
          entry.target.style.setProperty('--clp-ingredient-delay', `${index * 80}ms`);
          entry.target.classList.add('is-in');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px' });
      cards.forEach((card) => revealObserver.observe(card));
      root.classList.add('is-ready');
    }
    const button = root.querySelector('[data-clp-ingredients-toggle]');
    if (!button) return;
    button.addEventListener('click', () => {
      const open = root.classList.toggle('is-open');
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      root.querySelector('.clp-ingredients__rest')?.setAttribute('aria-hidden', open ? 'false' : 'true');
      const label = button.querySelector('[data-clp-ingredients-label]');
      if (label) label.textContent = open ? button.dataset.lessLabel : button.dataset.moreLabel;
    });
    const dialog = root.querySelector('[data-clp-label-dialog]');
    const closeDialog = () => {
      if (!dialog?.open || dialog.dataset.closing === 'true') return;
      dialog.dataset.closing = 'true';
      dialog.classList.remove('is-visible');
      window.setTimeout(() => {
        dialog.close();
        delete dialog.dataset.closing;
      }, 350);
    };
    root.querySelector('[data-clp-label-open]')?.addEventListener('click', () => {
      if (!dialog) return;
      dialog.showModal();
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => dialog.classList.add('is-visible')));
    });
    dialog?.querySelector('[data-clp-label-close]')?.addEventListener('click', closeDialog);
    dialog?.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });
  }

  function initComparison(root) {
    if (!root || root.dataset.comparisonInitialized === 'true') return;
    root.dataset.comparisonInitialized = 'true';
    const rail = root.querySelector('[data-clp-comparison-scroll]');
    const progress = root.querySelector('[data-clp-comparison-progress]');
    const thumb = root.querySelector('[data-clp-comparison-thumb]');
    const fade = root.querySelector('[data-clp-comparison-fade]');
    if (!rail || !progress || !thumb) return;

    const render = () => {
      const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const ratio = max ? rail.scrollLeft / max : 0;
      const thumbRatio = .38;
      progress.hidden = max < 2;
      thumb.style.width = `${thumbRatio * 100}%`;
      thumb.style.left = `${ratio * (1 - thumbRatio) * 100}%`;
      fade?.classList.toggle('is-hidden', !max || rail.scrollLeft >= max - 4);
    };

    rail.addEventListener('scroll', render, { passive: true });
    window.addEventListener('resize', render, { passive: true });
    requestAnimationFrame(render);
  }

  function initScience(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    const sticky = root.querySelector('[data-clp-science-sticky]');
    const intro = root.querySelector('[data-clp-science-intro]');
    const why = root.querySelector('[data-clp-science-why]');
    const stack = root.querySelector('[data-clp-science-stack]');
    const card = root.querySelector('[data-clp-science-card]');
    const cardShell = card?.querySelector('.clp-science-story__card-shell');
    const cta = root.querySelector('[data-clp-science-cta]');
    const pills = [...root.querySelectorAll('[data-clp-science-pill]')];
    const visuals = [...root.querySelectorAll('[data-clp-science-visual]')];
    const visualCopies = [...root.querySelectorAll('[data-clp-science-visual-copy]')];
    const panels = [...root.querySelectorAll('[data-clp-science-panel]')];
    const progressBars = [...root.querySelectorAll('[data-clp-science-progress]')];
    if (!sticky || !intro || !why || !stack || !card || !pills.length) return;

    let measured = false;
    let from = [];
    let to = [];
    let activeStage = null;
    let ticking = false;
    let mobileLift = 0;
    let shortScreen = false;

    const ease = (value) => value < .5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2;

    const showStage = (active) => {
      const shown = active < 0 ? 0 : active;
      if (shown !== activeStage) {
        visuals.forEach((item, index) => item.classList.toggle('is-active', index === shown));
        visualCopies.forEach((item, index) => {
          const isActive = index === shown;
          item.hidden = !isActive;
          item.classList.toggle('is-active', isActive);
        });
        panels.forEach((item, index) => {
          const isActive = index === shown;
          item.hidden = !isActive;
          item.classList.toggle('is-active', isActive);
        });
        activeStage = shown;
      }
      progressBars.forEach((bar, index) => bar.classList.toggle('is-complete', index <= shown));
      cta?.classList.toggle('is-visible', active === pills.length - 1);
    };

    const measure = () => {
      pills.forEach((pill) => { pill.style.transform = 'none'; });
      why.style.transform = 'none';
      const isMobile = window.innerWidth <= 860;
      if (isMobile) {
        why.style.top = '';
      } else {
        const whyRect = why.getBoundingClientRect();
        const stackOffset = stack.getBoundingClientRect().top - whyRect.top;
        const pillGap = 12;
        const pillsHeight = pills.reduce((height, pill) => height + pill.getBoundingClientRect().height, 0) + (pillGap * Math.max(0, pills.length - 1));
        const combinedHeight = stackOffset + pillsHeight;
        const cardCenterOffset = cardShell ? (card.offsetHeight - cardShell.offsetHeight) / 2 : 0;
        const cardCenter = (sticky.clientHeight / 2) - cardCenterOffset;
        why.style.top = `${Math.round(Math.max(56, cardCenter - (combinedHeight / 2)))}px`;
      }
      const stickyRect = sticky.getBoundingClientRect();
      const stackRect = stack.getBoundingClientRect();
      from = pills.map((pill) => {
        const rect = pill.getBoundingClientRect();
        return { x: rect.left - stickyRect.left, y: rect.top - stickyRect.top, height: rect.height };
      });
      let y = stackRect.top - stickyRect.top;
      const gap = window.innerWidth <= 860 ? 8 : 12;
      to = from.map((origin) => {
        const target = { x: stackRect.left - stickyRect.left, y };
        y += origin.height + gap;
        return target;
      });
      if (isMobile) {
        const cardTop = Math.round(y - gap + 12);
        card.style.top = `${cardTop}px`;
        let textHeight = 0;
        const hiddenStates = panels.map((panel) => panel.hidden);
        panels.forEach((panel) => {
          panel.hidden = false;
          textHeight = Math.max(textHeight, panel.getBoundingClientRect().height);
          panel.hidden = true;
        });
        panels.forEach((panel, index) => { panel.hidden = hiddenStates[index]; });
        const buyBar = document.querySelector('.clp-sticky-atc');
        const barReserve = buyBar ? buyBar.offsetHeight + (parseFloat(getComputedStyle(buyBar).bottom) || 12) + 16 : 24;
        root.classList.remove('is-short');
        const ctaSpace = cta ? cta.offsetHeight + 10 : 0;
        const minImageHeight = 84;
        shortScreen = cardTop + minImageHeight + textHeight + ctaSpace + barReserve > sticky.clientHeight;
        root.classList.toggle('is-short', shortScreen);
        const reserved = shortScreen ? 24 : barReserve;
        const ctaHeight = shortScreen ? 0 : ctaSpace;
        const imageHeight = Math.round(Math.min(240, Math.max(minImageHeight, sticky.clientHeight - cardTop - textHeight - ctaHeight - reserved)));
        card.style.setProperty('--clp-science-visual-height', `${imageHeight}px`);
        mobileLift = Math.max(0, cardTop + imageHeight + textHeight + ctaHeight + reserved - sticky.clientHeight);
      } else {
        mobileLift = 0;
        shortScreen = false;
        root.classList.remove('is-short');
        card.style.top = '50%';
        card.style.removeProperty('--clp-science-visual-height');
      }
      measured = true;
    };

    const render = () => {
      ticking = false;
      if (!measured) measure();
      const rect = root.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const total = Math.max(1, rect.height - viewportHeight);
      const progress = Math.min(1, Math.max(0, -rect.top / total));
      const pinned = rect.top <= 0 && rect.bottom >= viewportHeight;
      document.documentElement.classList.toggle('clp-science-hides-sticky', shortScreen && pinned);
      const transition = ease(Math.min(1, Math.max(0, progress / .2)));
      const lift = mobileLift * Math.max(0, (transition - .6) / .4);
      intro.style.opacity = String(1 - Math.min(1, transition * 1.6));
      intro.style.transform = `translateY(${-40 * transition}px)`;

      pills.forEach((pill, index) => {
        const origin = from[index];
        const target = to[index];
        if (!origin || !target) return;
        pill.style.transform = `translate(${Math.round((target.x - origin.x) * transition)}px,${Math.round((target.y - origin.y) * transition - lift)}px)`;
      });

      why.style.opacity = String(Math.max(0, (transition - .4) / .6));
      why.style.transform = `translateY(${Math.round(24 * (1 - transition) - lift)}px)`;
      const cardProgress = Math.max(0, (transition - .6) / .4);
      card.style.opacity = String(cardProgress);
      card.style.transform = `${window.innerWidth <= 860 ? '' : 'translateY(-50%) '}translateY(${Math.round(28 * (1 - cardProgress) - lift)}px)`;

      let active = -1;
      if (progress >= .2) active = Math.min(pills.length - 1, Math.floor((progress - .2) / .18));
      const morphed = transition >= 1;
      pills.forEach((pill, index) => pill.classList.toggle('is-active', morphed && index === active));
      showStage(active);
    };

    const requestRender = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(render);
    };

    pills.forEach((pill, index) => pill.addEventListener('click', () => {
      const top = root.getBoundingClientRect().top + window.scrollY;
      const total = root.offsetHeight - window.innerHeight;
      window.scrollTo({ top: top + total * (.2 + .18 * index + .09), behavior: 'smooth' });
    }));
    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', () => {
      measured = false;
      requestRender();
    }, { passive: true });
    window.setTimeout(() => {
      measured = false;
      requestRender();
    }, 250);
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        measured = false;
        requestRender();
      });
    }
    render();
  }

  function initResults(root) {
    if (!root || root.dataset.resultsInitialized === 'true') return;
    root.dataset.resultsInitialized = 'true';
    const track = root.querySelector('[data-clp-results-track]');
    if (!track) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let frame = null;
    const render = () => {
      frame = null;
      if (reducedMotion) {
        track.style.transform = 'translateX(0)';
        return;
      }
      const rect = root.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const progress = Math.max(0, Math.min(1, (viewportHeight - rect.top) / (viewportHeight + (rect.height * 2))));
      track.style.transform = `translateX(${(-progress * 340).toFixed(1)}px)`;
    };
    const requestRender = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(render);
    };
    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', requestRender, { passive: true });
    render();
  }

  function initFaqGroup(group) {
    if (!group || group.dataset.faqInitialized === 'true') return;
    group.dataset.faqInitialized = 'true';
    const items = [...group.querySelectorAll(':scope > details')];
    items.forEach((item) => item.addEventListener('toggle', () => {
      if (!item.open) return;
      items.forEach((other) => {
        if (other !== item) other.removeAttribute('open');
      });
    }));
  }

  function initLazyVideo(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    const video = root.querySelector('video[data-clp-lazy-video]');
    if (!video) return;
    const load = () => {
      if (video.dataset.loaded === 'true') return;
      [...video.querySelectorAll('source[data-src]')].forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.dataset.loaded = 'true';
      video.load();
      const promise = video.play();
      promise?.catch?.(() => {});
    };
    if (!('IntersectionObserver' in window)) {
      load();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      load();
      observer.disconnect();
    }, { rootMargin: '600px 0px' });
    observer.observe(root);
  }

  function initMobileStartCard(card) {
    const rail = card.parentElement;
    if (!rail || rail.dataset.startInitialized === 'true' || !window.matchMedia('(max-width: 749px)').matches) return;
    rail.dataset.startInitialized = 'true';
    const center = () => {
      rail.scrollLeft = card.offsetLeft - rail.offsetLeft - (rail.clientWidth - card.offsetWidth) / 2;
    };
    center();
    window.addEventListener('load', center, { once: true });
  }

  let detectedCountryRequest;
  function detectCountry() {
    detectedCountryRequest ||= fetch('/browsing_context_suggestions.json', { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`Country detection failed: ${response.status}`))))
      .then((data) => data?.detected_values?.country || null);
    return detectedCountryRequest;
  }

  function initGeoFlag(flag) {
    if (flag.dataset.geoInitialized === 'true') return;
    flag.dataset.geoInitialized = 'true';
    const image = flag.querySelector('img');
    let flagUrls = {};
    try {
      flagUrls = JSON.parse(flag.dataset.flagUrls || '{}');
    } catch (error) {
      console.warn('Invalid flag URL map', error);
    }
    detectCountry()
      .then((country) => {
        const url = country?.handle && (flagUrls[country.handle] || `https://cdn.shopify.com/static/images/flags/${country.handle.toLowerCase()}.svg?width=44`);
        if (!image || !url) return;
        image.removeAttribute('srcset');
        image.src = url;
        image.alt = country.name || country.handle;
      })
      .catch((error) => console.warn(error));
  }

  function initGeoShipping(element) {
    if (element.dataset.geoInitialized === 'true') return;
    element.dataset.geoInitialized = 'true';
    const countryTexts = new Map(
      (element.dataset.countryTexts || '')
        .split('~')
        .filter((entry) => entry.includes('|'))
        .map((entry) => {
          const [code, ...text] = entry.split('|');
          return [code.trim(), text.join('|').trim()];
        })
    );
    detectCountry()
      .then((country) => {
        if (!country?.handle) return;
        const text = countryTexts.get(country.handle) || element.dataset.defaultText;
        if (text) element.textContent = text;
      })
      .catch((error) => console.warn(error));
  }

  function initAddFeedback(root) {
    const form = root.querySelector('product-form form');
    const button = root.querySelector('[data-clp-add-button]');
    if (!form || !button || button.dataset.addFeedback === 'true') return;
    button.dataset.addFeedback = 'true';
    const minimumDuration = 700;
    const maximumDuration = 8000;
    let startedAt = 0;
    let fallbackTimer = 0;

    const finish = () => {
      if (!button.classList.contains('is-adding')) return;
      window.clearTimeout(fallbackTimer);
      const remaining = Math.max(0, minimumDuration - (performance.now() - startedAt));
      window.setTimeout(() => {
        button.classList.remove('is-adding');
        button.removeAttribute('aria-disabled');
      }, remaining);
    };

    form.addEventListener('submit', (event) => {
      if (button.classList.contains('is-adding')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (button.disabled || !form.checkValidity()) return;
      startedAt = performance.now();
      button.classList.add('is-adding');
      button.setAttribute('aria-disabled', 'true');
      fallbackTimer = window.setTimeout(finish, maximumDuration);
    }, { capture: true });
    document.addEventListener('cart:change', finish);
    form.addEventListener('cart:error', finish);
  }

  function initSupplementProduct(scope = document) {
    initProductHeader();
    scope.querySelectorAll('[data-clp-gallery]').forEach(initGallery);
    scope.querySelectorAll('[data-clp-product-main]').forEach(initProduct);
    scope.querySelectorAll('[data-clp-product-main]').forEach(initAddFeedback);
    scope.querySelectorAll('[data-clp-drag]').forEach(initDragRail);
    scope.querySelectorAll('[data-clp-auto-marquee]').forEach(initAutoMarquee);
    scope.querySelectorAll('[data-clp-ingredients]').forEach(initIngredients);
    scope.querySelectorAll('[data-clp-science]').forEach(initScience);
    scope.querySelectorAll('[data-clp-results]').forEach(initResults);
    scope.querySelectorAll('[data-clp-comparison]').forEach(initComparison);
    scope.querySelectorAll('[data-clp-video]').forEach(initLazyVideo);
    scope.querySelectorAll('[data-clp-reveal-heading]').forEach(initRevealHeading);
    scope.querySelectorAll('.clp-buy-faqs, .clp-faq__items').forEach(initFaqGroup);
    scope.querySelectorAll('[data-clp-mobile-start]').forEach(initMobileStartCard);
    scope.querySelectorAll('[data-clp-geo-flag]').forEach(initGeoFlag);
    scope.querySelectorAll('[data-clp-geo-shipping]').forEach(initGeoShipping);
  }

  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-clp-open-formula]')) return;
    document.querySelector('[data-clp-label-open]')?.click();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSupplementProduct());
  } else {
    initSupplementProduct();
  }
  document.addEventListener('shopify:section:load', (event) => initSupplementProduct(event.target));
})();
